import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, Landmark, Shield, Users, GraduationCap, Briefcase, ArrowRight, CheckCircle, BookOpen } from 'lucide-react';
import { issues } from '../data/mock';
import Newsletter from '../components/Newsletter';
import { useLanguage } from '../context/LanguageContext';

const iconMap = {
  Scale: Scale,
  Landmark: Landmark,
  Shield: Shield,
  Users: Users,
  GraduationCap: GraduationCap,
  Briefcase: Briefcase,
  BookOpen: BookOpen
};

const issueDetails = {
  1: { // Civil Rights & Justice
    priorities: [
      "Investigating and documenting civil and human rights violations",
      "Pursuing legal action against discriminatory practices",
      "Advocating for criminal justice reform",
      "Supporting victims of hate crimes and discrimination"
    ]
  },
  2: { // Indigenous Rights
    priorities: [
      "Protecting treaty rights and tribal sovereignty",
      "Defending sacred sites and ancestral lands",
      "Preserving indigenous languages and cultural practices",
      "Advocating for recognition of indigenous peoples"
    ]
  },
  3: { // Police Accountability
    priorities: [
      "Demanding independent investigations of police misconduct",
      "Supporting families affected by police violence",
      "Advocating for community oversight of law enforcement",
      "Pushing for policy reforms and accountability measures"
    ]
  },
  4: { // Immigration Justice
    priorities: [
      "Fighting against family separation policies",
      "Providing resources for those facing deportation",
      "Advocating for pathways to citizenship",
      "Opposing discriminatory immigration enforcement"
    ]
  },
  5: { // Education Equity
    priorities: [
      "Fighting for equitable school funding",
      "Promoting culturally relevant curriculum",
      "Addressing discipline disparities in schools",
      "Supporting access to higher education"
    ]
  },
  6: { // Economic Justice
    priorities: [
      "Advocating for fair wages and worker protections",
      "Fighting employment discrimination",
      "Promoting access to capital for entrepreneurs",
      "Addressing wealth inequality in our communities"
    ]
  }
};

const Programs = () => {
  const { t } = useLanguage();
  
  return (
    <div className="bg-stone-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50 to-stone-50" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-800 mb-6">
              {t('programs.heroTitle')}
            </h1>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              {t('programs.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Issues List */}
      <section className="py-12 relative bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {issues.map((issue, index) => {
              const IconComponent = iconMap[issue.icon];
              const details = issueDetails[issue.id];
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={issue.id}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${
                    !isEven ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className={!isEven ? 'lg:order-2' : ''}>
                    <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mb-6">
                      <IconComponent className="text-white" size={32} />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
                      {issue.title}
                    </h2>
                    <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                      {issue.description}
                    </p>
                    <h3 className="text-lg font-semibold text-teal-600 mb-4">Our Priorities:</h3>
                    <ul className="space-y-3">
                      {details.priorities.map((priority, i) => (
                        <li key={i} className="flex items-start gap-3 text-stone-700">
                          <CheckCircle className="text-teal-600 flex-shrink-0 mt-0.5" size={20} />
                          {priority}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className={`${!isEven ? 'lg:order-1' : ''} flex justify-center`}>
                    <div className="relative w-full max-w-md aspect-square">
                      <div className="absolute inset-0 bg-gradient-to-br from-stone-100 to-amber-50 rounded-3xl border border-stone-200 shadow-sm" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <IconComponent className="text-teal-600/20" size={180} />
                      </div>
                      <div className="absolute bottom-6 left-6 right-6">
                        <p className="text-teal-600 font-medium text-lg">{issue.stat}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24 relative bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
              How We <span className="text-teal-600">Work</span>
            </h2>
            <p className="text-lg text-stone-600">
              Our approach to creating systemic change
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Investigate',
                description: 'We thoroughly investigate reported violations and document evidence of systemic discrimination.'
              },
              {
                step: '02',
                title: 'Advocate',
                description: 'We advocate for policy changes and work with lawmakers to address identified issues.'
              },
              {
                step: '03',
                title: 'Litigate',
                description: 'When necessary, we pursue legal action to hold institutions accountable and protect rights.'
              },
              {
                step: '04',
                title: 'Organize',
                description: 'We build community power through grassroots organizing and collective action.'
              }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="text-6xl font-bold text-teal-600/30 mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-stone-800 mb-3">{item.title}</h3>
                <p className="text-stone-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6">
            {t('programs.getInvolved')}
          </h2>
          <p className="text-lg text-stone-600 mb-10 max-w-2xl mx-auto">
            {t('programs.getInvolvedText')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/report-violation"
              className="inline-flex items-center gap-2 px-8 py-4 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-all duration-300"
            >
              {t('programs.reportViolation')}
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/join"
              className="inline-flex items-center gap-2 px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-all duration-300"
            >
              {t('programs.joinXRU')}
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
